import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  BookOpen, PlayCircle, ChevronRight, Search, FileText, Mail, 
  Users, Settings, Shield, LayoutDashboard, Image, ClipboardList,
  Newspaper, Globe, Palette, Menu, Rocket, CheckCircle, ExternalLink,
  HelpCircle, Lightbulb, Video, Pencil, Plus, Trash2, X, Save
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import InteractiveTour from '../components/InteractiveTour';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HelpTutorials = () => {
  const [activeTab, setActiveTab] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTour, setShowTour] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [impersonatorRole, setImpersonatorRole] = useState('');
  const [videos, setVideos] = useState(null); // null = not loaded yet, [] = loaded
  const [tutorialsOverride, setTutorialsOverride] = useState({}); // saved article edits from DB
  const [playingVideo, setPlayingVideo] = useState(null); // { url, title }
  const [showEditor, setShowEditor] = useState(false);
  const [draftVideos, setDraftVideos] = useState([]);
  const [draftTutorials, setDraftTutorials] = useState({});
  const [editorTab, setEditorTab] = useState('videos'); // 'videos' | 'articles'
  const [saving, setSaving] = useState(false);

  const isMasterAdmin = userRole === 'master_admin' || impersonatorRole === 'master_admin';

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };
    // Load user role — also honours impersonation so a Master Admin who is
    // currently impersonating a tenant account still gets the Edit button.
    axios.get(`${API}/auth/me`, { headers })
      .then(r => {
        setUserRole(r.data?.role || '');
        setImpersonatorRole(r.data?.impersonator_role || '');
        // Diagnostic: leaves a footprint in devtools so we can debug role
        // visibility when a Master Admin can't see the Edit button.
        // eslint-disable-next-line no-console
        console.log('[Help] logged-in role =', r.data?.role, '| impersonator_role =', r.data?.impersonator_role);
      })
      .catch(() => {});
    // Load help content
    axios.get(`${API}/help/content`, { headers })
      .then(r => {
        setVideos(Array.isArray(r.data?.videos) ? r.data.videos : []);
        setTutorialsOverride(r.data?.tutorials && typeof r.data.tutorials === 'object' ? r.data.tutorials : {});
      })
      .catch(() => setVideos([])); // graceful fallback
  }, []);

  const openEditor = () => {
    setDraftVideos((videos || []).map(v => ({ ...v })));
    // Seed article drafts from merged tutorials (defaults + overrides) so
    // even topics that have never been edited appear in the dialog with
    // their current content — user can edit any of them.
    const seed = {};
    Object.entries(defaultTutorials).forEach(([key, t]) => {
      const ov = tutorialsOverride?.[key] || {};
      seed[key] = {
        title: ov.title || t.title,
        description: ov.description || t.description,
        sections: (ov.sections || t.sections || []).map(s => ({ title: s.title || '', content: s.content || '' })),
      };
    });
    setDraftTutorials(seed);
    setEditorTab('videos');
    setShowEditor(true);
  };

  const addDraftVideo = () => {
    setDraftVideos(prev => ([...prev, { id: `v${Date.now()}`, title: 'New Video', url: '', duration: '', category: '' }]));
  };

  const updateDraftVideo = (idx, patch) => {
    setDraftVideos(prev => prev.map((v, i) => i === idx ? { ...v, ...patch } : v));
  };

  const removeDraftVideo = (idx) => {
    setDraftVideos(prev => prev.filter((_, i) => i !== idx));
  };

  // ─── Article draft helpers ───────────────────────────────────────────
  const updateDraftTopic = (key, patch) => {
    setDraftTutorials(prev => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };
  const updateDraftSection = (key, idx, patch) => {
    setDraftTutorials(prev => {
      const topic = { ...(prev[key] || {}) };
      const sections = (topic.sections || []).map((s, i) => i === idx ? { ...s, ...patch } : s);
      return { ...prev, [key]: { ...topic, sections } };
    });
  };
  const addDraftSection = (key) => {
    setDraftTutorials(prev => {
      const topic = { ...(prev[key] || {}) };
      const sections = [...(topic.sections || []), { title: 'New Section', content: '' }];
      return { ...prev, [key]: { ...topic, sections } };
    });
  };
  const removeDraftSection = (key, idx) => {
    setDraftTutorials(prev => {
      const topic = { ...(prev[key] || {}) };
      const sections = (topic.sections || []).filter((_, i) => i !== idx);
      return { ...prev, [key]: { ...topic, sections } };
    });
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = { videos: draftVideos, tutorials: draftTutorials };
      const r = await axios.put(`${API}/help/content`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setVideos(Array.isArray(r.data?.videos) ? r.data.videos : draftVideos);
      setTutorialsOverride(r.data?.tutorials && typeof r.data.tutorials === 'object' ? r.data.tutorials : draftTutorials);
      setShowEditor(false);
      toast.success('Help & Tutorials updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save. Master Admin only.');
    } finally {
      setSaving(false);
    }
  };

  // Convert any URL into an embeddable player URL for the modal.
  const toEmbedUrl = (raw) => {
    if (!raw) return '';
    try {
      const u = new URL(raw);
      // YouTube: convert to /embed/
      if (u.hostname.includes('youtube.com')) {
        const id = u.searchParams.get('v');
        if (id) return `https://www.youtube.com/embed/${id}?autoplay=1`;
      }
      if (u.hostname === 'youtu.be') {
        return `https://www.youtube.com/embed${u.pathname}?autoplay=1`;
      }
      // Vimeo: convert to player.vimeo.com/video/
      if (u.hostname.includes('vimeo.com')) {
        const m = u.pathname.match(/(\d+)/);
        if (m) return `https://player.vimeo.com/video/${m[1]}?autoplay=1`;
      }
      // Loom
      if (u.hostname.includes('loom.com') && u.pathname.includes('/share/')) {
        return raw.replace('/share/', '/embed/');
      }
      return raw;
    } catch {
      return raw;
    }
  };
  const isDirectVideo = (raw) => /\.(mp4|webm|ogg)(\?|#|$)/i.test(raw || '');

  const defaultTutorials = {
    'getting-started': {
      title: 'Getting Started',
      icon: Rocket,
      color: 'indigo',
      description: 'Learn the basics of NexusCMS and get up and running quickly.',
      videoUrl: '', // Placeholder for video URL
      sections: [
        {
          title: 'Welcome to NexusCMS',
          content: `NexusCMS is a powerful multi-tenant content management system designed for creating websites, managing newsletters, and engaging with your audience.

**Key Features:**
• Create and manage web pages with a visual editor
• Design and send beautiful newsletters
• Manage your media library
• Invite team members and manage permissions
• Customize your site theme and branding
• Track analytics and engagement`
        },
        {
          title: 'Your First Steps',
          content: `**Step 1: Explore the Dashboard**
The dashboard gives you an overview of your content, including pages, newsletters, and team activity.

**Step 2: Create Your First Page**
Go to Pages → Click "New Page" → Add your content using the block editor.

**Step 3: Set Up Your Theme**
Visit Site Theme to customize colors, fonts, and branding.

**Step 4: Invite Your Team**
Add team members under the Team section to collaborate.`
        },
        {
          title: 'Understanding the Interface',
          content: `**Sidebar Navigation:**
• Dashboard - Overview and quick stats
• Pages - Create and manage web pages
• Newsletters - Email campaigns and subscribers
• Media Library - Upload and organize files
• Team - Manage team members
• Settings - Account and preferences

**Quick Actions:**
Use the buttons in the top-right of most pages for quick access to common tasks like creating new content.`
        }
      ]
    },
    'pages': {
      title: 'Pages & Content',
      icon: FileText,
      color: 'blue',
      description: 'Learn how to create, edit, and publish web pages.',
      videoUrl: '',
      sections: [
        {
          title: 'Creating a New Page',
          content: `**To create a new page:**
1. Navigate to Pages from the sidebar
2. Click the "New Page" button
3. Enter a title for your page
4. Use the block editor to add content
5. Click "Save" to save as draft or "Publish" to make it live

**Page Settings:**
• Title - The page title shown in browsers
• Slug - The URL path for the page
• Status - Draft or Published`
        },
        {
          title: 'Using the Block Editor',
          content: `**Available Blocks:**
• Text Block - Rich text content with formatting
• Image Block - Upload or select images
• Video Block - Embed videos from URLs
• Button Block - Add call-to-action buttons
• Spacer Block - Add vertical spacing
• Divider Block - Add horizontal lines

**Tips:**
• Drag blocks to reorder them
• Click on a block to edit its content
• Use the delete icon to remove blocks`
        },
        {
          title: 'Publishing & Preview',
          content: `**Preview Mode:**
Click the "Preview" button to see how your page will look to visitors.

**Publishing:**
1. Make sure all content is complete
2. Click "Publish" to make the page live
3. Published pages are accessible via their URL slug

**Unpublishing:**
Change the status back to "Draft" to hide a page from visitors.`
        }
      ]
    },
    'newsletters': {
      title: 'Newsletters',
      icon: Mail,
      color: 'emerald',
      description: 'Create and send email newsletters to your subscribers.',
      videoUrl: '',
      sections: [
        {
          title: 'Creating a Newsletter',
          content: `**To create a newsletter:**
1. Go to Newsletters from the sidebar
2. Click "New Newsletter"
3. Enter a subject line
4. Use the editor to design your content
5. Preview before sending

**Newsletter Editor:**
The newsletter editor works similarly to the page editor, with blocks optimized for email.`
        },
        {
          title: 'Managing Subscribers',
          content: `**Adding Subscribers:**
• Individual: Add subscribers one by one
• Bulk Import: Upload a CSV file with email addresses
• Forms: Use embedded forms to collect signups

**Subscriber Groups:**
Organize subscribers into groups for targeted campaigns.

**Managing Preferences:**
Subscribers can update their preferences or unsubscribe via links in your emails.`
        },
        {
          title: 'Sending & Analytics',
          content: `**Sending Options:**
• Send Now - Immediately send to all subscribers
• Send Test - Send a test email to yourself first

**Analytics:**
Track your newsletter performance:
• Open Rate - How many recipients opened the email
• Click Rate - Links clicked within the email
• Bounces - Emails that couldn't be delivered
• Unsubscribes - People who opted out`
        }
      ]
    },
    'subscribers': {
      title: 'Subscriber Management',
      icon: Users,
      color: 'purple',
      description: 'Manage your newsletter subscribers and groups.',
      videoUrl: '',
      sections: [
        {
          title: 'Adding Subscribers',
          content: `**Manual Addition:**
1. Go to Newsletters → Subscribers tab
2. Click "Add Subscriber"
3. Enter email and optional name
4. Assign to groups if needed

**Bulk Import:**
1. Prepare a CSV file with columns: email, name (optional)
2. Click "Import" and upload your file
3. Review and confirm the import`
        },
        {
          title: 'Subscriber Groups',
          content: `**Creating Groups:**
Groups help you segment your audience for targeted campaigns.

1. Go to the Groups tab
2. Click "New Group"
3. Name your group (e.g., "VIP Customers", "New Signups")
4. Add subscribers to the group

**Using Groups:**
When sending newsletters, you can choose to send to specific groups instead of all subscribers.`
        },
        {
          title: 'Managing Subscriber Data',
          content: `**Viewing Details:**
Click on any subscriber to see their full profile, including subscription date and engagement history.

**Editing Subscribers:**
• Update email addresses
• Change group memberships
• Add notes or tags

**Removing Subscribers:**
Delete subscribers who request removal or mark them as unsubscribed to maintain your list hygiene.`
        }
      ]
    },
    'forms': {
      title: 'Forms & Articles',
      icon: ClipboardList,
      color: 'amber',
      description: 'Create forms and write articles for your site.',
      videoUrl: '',
      sections: [
        {
          title: 'Building Forms',
          content: `**Creating a Form:**
1. Navigate to Forms
2. Click "New Form"
3. Add fields using the form builder
4. Configure submission settings
5. Save and embed on your pages

**Field Types:**
• Text Input - Single line text
• Text Area - Multi-line text
• Email - Email validation included
• Number - Numeric input
• Dropdown - Select from options
• Checkbox - Yes/no options`
        },
        {
          title: 'Form Submissions',
          content: `**Viewing Submissions:**
All form submissions are collected and viewable in the Forms section.

**Export Options:**
Download submissions as CSV for further analysis.

**Notifications:**
Set up email notifications to receive alerts when forms are submitted.`
        },
        {
          title: 'Writing Articles',
          content: `**Creating Articles:**
1. Go to Articles
2. Click "New Article"
3. Write your content using the rich text editor
4. Add featured image and tags
5. Publish when ready

**Article Features:**
• Categories and tags for organization
• Featured images
• SEO-friendly URLs
• Draft and publish states`
        }
      ]
    },
    'team': {
      title: 'Team Management',
      icon: Users,
      color: 'cyan',
      description: 'Invite team members and manage permissions.',
      videoUrl: '',
      sections: [
        {
          title: 'Inviting Team Members',
          content: `**To invite someone:**
1. Go to Team from the sidebar
2. Click "Invite Member"
3. Enter their email address
4. Select their role
5. Send the invitation

**The invitee will receive an email with instructions to join your workspace.**`
        },
        {
          title: 'Roles & Permissions',
          content: `**Available Roles:**

• **Admin** - Full access to all features
• **Editor** - Can create and edit content
• **Author** - Can create content but limited editing
• **Viewer** - Read-only access

**Note:** Only Admins can invite new members and manage settings.`
        },
        {
          title: 'Managing Team',
          content: `**Changing Roles:**
Click on a team member to change their role or permissions.

**Removing Members:**
Admins can remove team members who no longer need access.

**Activity Tracking:**
View team activity in the audit log (Security Settings) to see who made changes.`
        }
      ]
    },
    'security': {
      title: 'Security Settings',
      icon: Shield,
      color: 'red',
      description: 'Secure your account with 2FA and review audit logs.',
      videoUrl: '',
      sections: [
        {
          title: 'Two-Factor Authentication',
          content: `**Enabling 2FA:**
1. Go to Security Settings
2. Click "Enable 2FA"
3. Scan the QR code with an authenticator app (Google Authenticator, Authy, etc.)
4. Enter the verification code
5. Save your backup codes securely

**Why Use 2FA?**
Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password.`
        },
        {
          title: 'Backup Codes',
          content: `**About Backup Codes:**
Backup codes are one-time use codes that let you log in if you lose access to your authenticator app.

**Important:**
• Store backup codes in a safe place
• Each code can only be used once
• Generate new codes if you run out
• Never share your backup codes`
        },
        {
          title: 'Audit Logs',
          content: `**What's Tracked:**
• Login attempts (successful and failed)
• Password changes
• 2FA setup/removal
• Team member changes
• Critical settings modifications

**Viewing Logs:**
Go to Security Settings → Audit Logs tab to review all security-related activity.`
        }
      ]
    }
  };

  // Merge defaults with Master-Admin overrides from the API. Icons/colors/keys
  // stay from defaults (they're JSX components); title/description/sections come
  // from the override when present.
  const tutorials = Object.fromEntries(
    Object.entries(defaultTutorials).map(([key, t]) => {
      const ov = tutorialsOverride?.[key];
      if (!ov) return [key, t];
      return [key, {
        ...t,
        title: ov.title || t.title,
        description: ov.description || t.description,
        sections: Array.isArray(ov.sections) && ov.sections.length ? ov.sections : t.sections,
      }];
    })
  );

  const faqs = [
    {
      question: 'How do I reset my password?',
      answer: 'Go to Settings → Account and click "Change Password". You\'ll need to enter your current password and then your new password twice to confirm.'
    },
    {
      question: 'Can I use my own domain?',
      answer: 'Yes! Go to Custom Domain settings (available for Admin users) to configure your own domain. You\'ll need to update your DNS settings to point to our servers.'
    },
    {
      question: 'How many team members can I add?',
      answer: 'The number of team members depends on your plan. Free plans allow up to 3 members, Starter allows 10, Pro allows 25, and Enterprise has unlimited team members.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes! We use AES-256 encryption for data at rest, TLS 1.3 for data in transit, and offer Two-Factor Authentication. We\'re GDPR compliant and follow industry best practices.'
    },
    {
      question: 'How do I export my data?',
      answer: 'You can export pages as HTML, subscribers as CSV, and form submissions as CSV. Go to each respective section and look for the Export option.'
    },
    {
      question: 'What email providers do you support for newsletters?',
      answer: 'We use SendGrid for reliable email delivery. Your newsletters will be sent from your configured sender address with proper authentication (SPF, DKIM).'
    }
  ];

  const filteredTutorials = Object.entries(tutorials).filter(([key, tutorial]) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      tutorial.title.toLowerCase().includes(query) ||
      tutorial.description.toLowerCase().includes(query) ||
      tutorial.sections.some(s => 
        s.title.toLowerCase().includes(query) || 
        s.content.toLowerCase().includes(query)
      )
    );
  });

  return (
    <div className="flex">
      <Sidebar />
      
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  <BookOpen className="text-indigo-600" size={32} />
                  Help & Tutorials
                </h1>
                <p className="text-slate-500 mt-2">
                  Learn how to use NexusCMS with guides, tutorials, and FAQs
                </p>
              </div>
              <div className="flex items-center gap-2">
                {isMasterAdmin && (
                  <Button
                    onClick={openEditor}
                    variant="outline"
                    className="gap-2 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    data-testid="help-master-edit-btn"
                  >
                    <Pencil size={16} />
                    Edit Content
                  </Button>
                )}
                <Button onClick={() => setShowTour(true)} className="gap-2">
                  <PlayCircle size={18} />
                  Start Interactive Tour
                </Button>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative mb-8">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
            <Input
              placeholder="Search tutorials and guides..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-white"
            />
          </div>

          {/* Quick Start Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div 
              onClick={() => setShowTour(true)}
              className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
            >
              <PlayCircle size={32} className="mb-3" />
              <h3 className="font-semibold text-lg">Interactive Tour</h3>
              <p className="text-indigo-100 text-sm mt-1">Get a guided walkthrough of the platform</p>
            </div>
            <div 
              onClick={() => setActiveTab('getting-started')}
              className="bg-white border border-slate-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
            >
              <Rocket size={32} className="mb-3 text-emerald-600" />
              <h3 className="font-semibold text-lg">Getting Started</h3>
              <p className="text-slate-500 text-sm mt-1">New here? Start with the basics</p>
            </div>
            <div 
              onClick={() => setActiveTab('video-tutorials')}
              className="bg-white border border-slate-200 rounded-xl p-6 cursor-pointer hover:shadow-lg transition-all"
            >
              <Video size={32} className="mb-3 text-red-500" />
              <h3 className="font-semibold text-lg">Video Tutorials</h3>
              <p className="text-slate-500 text-sm mt-1">Watch step-by-step video guides</p>
            </div>
          </div>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6 flex-wrap h-auto gap-1">
              {Object.entries(tutorials).map(([key, tutorial]) => {
                const Icon = tutorial.icon;
                return (
                  <TabsTrigger key={key} value={key} className="gap-2">
                    <Icon size={16} />
                    {tutorial.title}
                  </TabsTrigger>
                );
              })}
              <TabsTrigger value="video-tutorials" className="gap-2">
                <Video size={16} />
                Videos
              </TabsTrigger>
              <TabsTrigger value="faq" className="gap-2">
                <HelpCircle size={16} />
                FAQ
              </TabsTrigger>
            </TabsList>

            {/* Tutorial Content */}
            {filteredTutorials.map(([key, tutorial]) => {
              const Icon = tutorial.icon;
              return (
                <TabsContent key={key} value={key}>
                  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    {/* Tutorial Header */}
                    <div className={`bg-${tutorial.color}-50 border-b border-${tutorial.color}-100 p-6`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-xl bg-${tutorial.color}-100 flex items-center justify-center`}>
                          <Icon className={`text-${tutorial.color}-600`} size={28} />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold">{tutorial.title}</h2>
                          <p className="text-slate-600 mt-1">{tutorial.description}</p>
                        </div>
                      </div>
                    </div>

                    {/* Video Embed Placeholder */}
                    {tutorial.videoUrl && (
                      <div className="p-6 border-b border-slate-200">
                        <div className="aspect-video bg-slate-900 rounded-lg flex items-center justify-center">
                          <div className="text-center text-white">
                            <PlayCircle size={48} className="mx-auto mb-2 opacity-80" />
                            <p>Video Tutorial</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Sections */}
                    <div className="p-6">
                      <Accordion type="single" collapsible className="w-full">
                        {tutorial.sections.map((section, index) => (
                          <AccordionItem key={index} value={`section-${index}`}>
                            <AccordionTrigger className="text-left">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-sm font-medium">
                                  {index + 1}
                                </div>
                                <span className="font-medium">{section.title}</span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent>
                              <div className="pl-11 prose prose-slate max-w-none">
                                {section.content.split('\n').map((line, i) => {
                                  if (line.startsWith('**') && line.endsWith('**')) {
                                    return <h4 key={i} className="font-semibold text-slate-900 mt-4 mb-2">{line.replace(/\*\*/g, '')}</h4>;
                                  }
                                  if (line.startsWith('• ')) {
                                    return <li key={i} className="text-slate-600 ml-4">{line.substring(2)}</li>;
                                  }
                                  if (line.trim() === '') {
                                    return <br key={i} />;
                                  }
                                  return <p key={i} className="text-slate-600 mb-2">{line}</p>;
                                })}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    </div>
                  </div>
                </TabsContent>
              );
            })}

            {/* Video Tutorials Tab */}
            <TabsContent value="video-tutorials">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-red-100 flex items-center justify-center">
                    <Video className="text-red-600" size={28} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Video Tutorials</h2>
                    <p className="text-slate-600 mt-1">Watch step-by-step video guides</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {(videos || []).map((video, index) => {
                    const clickable = !!video.url;
                    return (
                      <div
                        key={video.id || index}
                        onClick={clickable ? () => setPlayingVideo(video) : undefined}
                        className={`border border-slate-200 rounded-lg overflow-hidden transition-all ${clickable ? 'cursor-pointer hover:shadow-md hover:border-indigo-300' : 'opacity-80'}`}
                        data-testid={`help-video-card-${index}`}
                      >
                        <div className="aspect-video bg-slate-900 flex items-center justify-center relative group">
                          <PlayCircle size={48} className={`text-white transition-opacity ${clickable ? 'opacity-80 group-hover:opacity-100' : 'opacity-40'}`} />
                          {video.duration && (
                            <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                              {video.duration}
                            </span>
                          )}
                          {!clickable && (
                            <span className="absolute top-2 left-2 bg-amber-500/90 text-white text-[10px] font-semibold px-2 py-0.5 rounded uppercase tracking-wide">
                              Coming soon
                            </span>
                          )}
                        </div>
                        <div className="p-4">
                          {video.category && <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">{video.category}</span>}
                          <h3 className="font-medium mt-2">{video.title}</h3>
                        </div>
                      </div>
                    );
                  })}
                  {videos && videos.length === 0 && (
                    <div className="col-span-2 p-8 text-center text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
                      No videos yet.
                      {isMasterAdmin && (
                        <div className="mt-2">
                          <Button size="sm" onClick={openEditor} data-testid="help-videos-add-first"><Plus size={14} className="mr-1" /> Add your first video</Button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            {/* FAQ Tab */}
            <TabsContent value="faq">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-14 h-14 rounded-xl bg-amber-100 flex items-center justify-center">
                    <HelpCircle className="text-amber-600" size={28} />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
                    <p className="text-slate-600 mt-1">Quick answers to common questions</p>
                  </div>
                </div>

                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`faq-${index}`}>
                      <AccordionTrigger className="text-left">
                        <span className="font-medium">{faq.question}</span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <p className="text-slate-600">{faq.answer}</p>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>

                <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                  <div className="flex items-start gap-3">
                    <HelpCircle className="text-indigo-500 flex-shrink-0 mt-0.5" size={20} />
                    <div>
                      <p className="font-medium text-indigo-900">Still have questions?</p>
                      <p className="text-sm text-indigo-700 mt-1">
                        Contact our support team at <a href="mailto:support@nexuscms.com" className="underline">support@nexuscms.com</a>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Interactive Tour */}
      {showTour && <InteractiveTour onClose={() => setShowTour(false)} />}

      {/* Video Player Modal — opens when a card with a URL is clicked */}
      <Dialog open={!!playingVideo} onOpenChange={(o) => !o && setPlayingVideo(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-black">
          <DialogHeader className="p-4 bg-slate-900 text-white flex-row items-center justify-between space-y-0">
            <DialogTitle className="text-white text-base">{playingVideo?.title}</DialogTitle>
          </DialogHeader>
          {playingVideo && (
            <div className="aspect-video bg-black">
              {isDirectVideo(playingVideo.url) ? (
                <video src={playingVideo.url} controls autoPlay className="w-full h-full" data-testid="help-video-player" />
              ) : (
                <iframe
                  src={toEmbedUrl(playingVideo.url)}
                  title={playingVideo.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                  data-testid="help-video-iframe"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Master-Admin-Only Edit Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={18} className="text-indigo-600" />
              Edit Help & Tutorials Content
            </DialogTitle>
            <p className="text-sm text-slate-500">
              Master Admin only. Update Videos and Articles for the platform&apos;s Help &amp; Tutorials page.
            </p>
          </DialogHeader>

          <Tabs value={editorTab} onValueChange={setEditorTab} className="mt-3">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="videos" data-testid="editor-tab-videos">
                <Video size={14} className="mr-2" /> Videos ({draftVideos.length})
              </TabsTrigger>
              <TabsTrigger value="articles" data-testid="editor-tab-articles">
                <FileText size={14} className="mr-2" /> Articles ({Object.keys(draftTutorials).length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="videos" className="space-y-3 mt-4">
              {draftVideos.map((v, idx) => (
                <div key={v.id || idx} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50" data-testid={`help-video-edit-row-${idx}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-slate-500">Video #{idx + 1}</span>
                    <button onClick={() => removeDraftVideo(idx)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`help-video-delete-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <Input placeholder="Title" value={v.title || ''} onChange={(e) => updateDraftVideo(idx, { title: e.target.value })} data-testid={`help-video-title-${idx}`} />
                  <Input placeholder="Video URL (YouTube / Vimeo / Loom / .mp4)" value={v.url || ''} onChange={(e) => updateDraftVideo(idx, { url: e.target.value })} data-testid={`help-video-url-${idx}`} />
                  <div className="grid grid-cols-2 gap-2">
                    <Input placeholder="Duration (e.g. 5:30)" value={v.duration || ''} onChange={(e) => updateDraftVideo(idx, { duration: e.target.value })} data-testid={`help-video-duration-${idx}`} />
                    <Input placeholder="Category (e.g. Pages)" value={v.category || ''} onChange={(e) => updateDraftVideo(idx, { category: e.target.value })} data-testid={`help-video-category-${idx}`} />
                  </div>
                </div>
              ))}
              <Button onClick={addDraftVideo} variant="outline" className="w-full gap-2" data-testid="help-video-add">
                <Plus size={16} />
                Add Video
              </Button>
            </TabsContent>

            <TabsContent value="articles" className="space-y-4 mt-4">
              <p className="text-xs text-slate-500">
                Formatting: use <code className="bg-slate-100 px-1 rounded">**bold**</code> for bold text and lines starting with <code className="bg-slate-100 px-1 rounded">• </code> for bullet points. Blank lines separate paragraphs. Icons and colours are set in code and stay as they are.
              </p>
              <Accordion type="multiple" className="space-y-2">
                {Object.entries(draftTutorials).map(([key, topic]) => (
                  <AccordionItem key={key} value={key} className="border border-slate-200 rounded-lg bg-slate-50 px-3">
                    <AccordionTrigger className="hover:no-underline py-3" data-testid={`article-topic-${key}`}>
                      <span className="text-sm font-semibold">{topic.title || key}</span>
                    </AccordionTrigger>
                    <AccordionContent className="space-y-3 pb-3">
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-600">Topic Title</label>
                        <Input value={topic.title || ''} onChange={(e) => updateDraftTopic(key, { title: e.target.value })} data-testid={`article-topic-title-${key}`} />
                      </div>
                      <div className="space-y-1">
                        <label className="text-xs font-semibold text-slate-600">Topic Description</label>
                        <Input value={topic.description || ''} onChange={(e) => updateDraftTopic(key, { description: e.target.value })} data-testid={`article-topic-desc-${key}`} />
                      </div>
                      <div className="space-y-2">
                        <label className="text-xs font-semibold text-slate-600">Sections</label>
                        {(topic.sections || []).map((s, sidx) => (
                          <div key={sidx} className="border border-slate-200 rounded-md p-2 bg-white space-y-2" data-testid={`article-section-${key}-${sidx}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-xs font-medium text-slate-500">Section #{sidx + 1}</span>
                              <button onClick={() => removeDraftSection(key, sidx)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`article-section-delete-${key}-${sidx}`}>
                                <Trash2 size={12} />
                              </button>
                            </div>
                            <Input placeholder="Section title" value={s.title || ''} onChange={(e) => updateDraftSection(key, sidx, { title: e.target.value })} data-testid={`article-section-title-${key}-${sidx}`} />
                            <textarea
                              placeholder="Section body — use **bold** and • bullets"
                              value={s.content || ''}
                              onChange={(e) => updateDraftSection(key, sidx, { content: e.target.value })}
                              rows={8}
                              className="w-full text-sm rounded-md border border-slate-200 p-2 font-mono resize-y"
                              data-testid={`article-section-content-${key}-${sidx}`}
                            />
                          </div>
                        ))}
                        <Button onClick={() => addDraftSection(key)} variant="outline" size="sm" className="gap-1" data-testid={`article-section-add-${key}`}>
                          <Plus size={14} /> Add Section
                        </Button>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setShowEditor(false)} data-testid="help-editor-cancel">Cancel</Button>
            <Button onClick={saveEdits} disabled={saving} className="gap-2" data-testid="help-editor-save">
              <Save size={16} />
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HelpTutorials;
